import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalDirective } from '@shared/directives/modal.directive';
import { ICustomNotification } from '@shared/interfaces/custom-notification.interface';
import { EmailNotification } from '@shared/models/email-notification.model';
import { CustomNotificationsService } from '@shared/services/custom-notifications.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';

/**
 * Per-address recipient list of the legacy hiring-leads notification: each
 * address picks the lead types (candidate / hiring) it hears about. Opened
 * from the "Additional emails" cell of the Team notifications matrix.
 */
@Component({
  selector: 'app-modals-custom-notifications',
  templateUrl: './modals.custom-notifications.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ModalsCustomNotificationsComponent
  extends ModalDirective<ICustomNotification>
  implements OnInit
{
  public submitKey = String('BUTTONS.SAVE');
  public isLoading = Boolean(true);
  public isButtonsDisabled = Boolean(false);
  public entry: FormGroup;
  public title: string;
  public row?: ICustomNotification;

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _customNotifications: CustomNotificationsService,
    private readonly _logger: LoggerService,
    private readonly _toast: ToastService
  ) {
    super();
  }

  get email_notifications() {
    return this.entry.get('email_notifications') as FormArray;
  }

  addEmailField() {
    this.email_notifications.push(this.createEmailField());
  }

  removeEmailField(index: number) {
    this.email_notifications.removeAt(index);
  }

  ngOnInit(): void {
    this.createForm();

    this.openModal.subscribe((row: ICustomNotification) => {
      this.row = row;
      this.populateEmailFields(row.email_notifications || []);
      this.isLoading = false;
    });
  }

  public onDismiss() {
    this.errors = [];
  }

  public onSubmit({ valid, value }: { valid: boolean; value: { email_notifications: EmailNotification[] } }): void {
    if (!valid || !this.row) {
      return;
    }

    this.isButtonsDisabled = true;

    const url = `PATCH /custom_notifications/${this.row.id}`;
    this._customNotifications
      .patch(this.row.id, {
        send_to_custom: true,
        email_notifications: value.email_notifications
      })
      .subscribe({
        next: (res: ICustomNotification) => {
          this._logger.info(this.constructorName, url, res);

          this._toast.success('TOASTS.NOTIFICATIONS_UPDATED');
          this.isButtonsDisabled = false;
          this.resetModal(res);
        },
        error: (err: any) => {
          this._logger.error(this.constructorName, url, err);

          this.isButtonsDisabled = false;
          this._toast.error('TOASTS.NOTIFICATIONS_UPDATE_FAILED');
        }
      });
  }

  protected createForm(): void {
    this.entry = this._fb.group({
      email_notifications: this._fb.array([this.createEmailField()])
    });
  }

  private emailTypeValidator(formGroup: FormGroup): { [key: string]: any } | null {
    const isHiringType = formGroup.get('is_hiring_type').value;
    const isCandidateType = formGroup.get('is_candidate_type').value;
    return isHiringType || isCandidateType ? null : { typeNotSelected: true };
  }

  private populateEmailFields(emails: EmailNotification[]) {
    const emailFormGroups = emails.map((email) => this.createEmailField(email));
    this.entry.setControl('email_notifications', this._fb.array(emailFormGroups));
  }

  private createEmailField(email: EmailNotification = new EmailNotification()): FormGroup {
    return this._fb.group(
      {
        email: [email.email || email.address || '', [Validators.required, Validators.email]],
        is_hiring_type: [!!email.is_hiring_type],
        is_candidate_type: [!!email.is_candidate_type]
      },
      { validators: this.emailTypeValidator }
    );
  }
}
